import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetAviatorRoundBetsQuery } from '../services/aviator/aviatorApi';

const formatTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const AviatorRoundBetsPage = () => {
  const navigate = useNavigate();
  const { roundId } = useParams();

  const { data, isLoading, isFetching, error } = useGetAviatorRoundBetsQuery(
    { roundId },
    { skip: !roundId }
  );

  const rawData = data?.data;
  const bets = Array.isArray(rawData)
    ? rawData
    : rawData
      ? [
          ...(rawData.pending   || []),
          ...(rawData.cashedout || []),
          ...(rawData.lost      || []),
        ]
      : [];

  const summary  = data?.summary || {};
  const colorPL  = (v) => Number(v) >= 0 ? 'text-green-600' : 'text-red-500';
  const fmtPL    = (v) => v != null ? Number(v).toFixed(2) : '-';

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-6xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            <div className="bg-[#1a1c2e] px-5 py-3 flex items-center justify-between">
              <div>
                <span className="text-xl text-white font-normal">Round Bets</span>
                <div className="text-xs text-gray-400 font-mono mt-0.5">{roundId}</div>
              </div>
              <button onClick={() => navigate(-1)} className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors">
                Back
              </button>
            </div>

            {/* Summary */}
            {summary.totalBets != null ? (
              <div className="flex gap-6 px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm flex-wrap">
                <span className="text-gray-500">Total Bets: <strong className="text-gray-800">{summary.totalBets ?? 0}</strong></span>
                <span className="text-gray-500">Total Stake: <strong className="text-gray-800">{summary.totalStake ?? 0}</strong></span>
                <span className="text-gray-500">P/L: <strong className={colorPL(summary.pl)}>{fmtPL(summary.pl)}</strong></span>
                {summary.profit != null && <span className="text-gray-500">Profit: <strong className="text-green-600">{summary.profit}</strong></span>}
                {summary.loss != null && <span className="text-gray-500">Loss: <strong className="text-red-500">{summary.loss}</strong></span>}
              </div>
            ) : null}

            <div className="overflow-x-auto">
              {isLoading || isFetching ? (
                <div className="flex justify-center py-8"><Loading /></div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#1a1c2e] text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">#</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Client</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Stake</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Auto Cashout</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Cashout @</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">P/L</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Status</th>
                      <th className="py-3 px-4 text-left font-medium">Placed At</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {bets.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-gray-400">No bets found</td>
                      </tr>
                    ) : (
                      bets.map((bet, idx) => (
                        <tr key={bet._id || idx} className={idx % 2 === 0 ? 'bg-white border-t border-gray-100' : 'bg-gray-50 border-t border-gray-100'}>
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-500">{idx + 1}</td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            <div className="font-semibold text-gray-800">{bet.fullName || bet.username || '-'}</div>
                            {bet.userId && <div className="text-xs text-blue-500">{bet.userId}</div>}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-700">{bet.stake ?? '-'}</td>
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-700">
                            {bet.autoCashout ? `${bet.autoCashout}x` : '-'}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200 font-semibold text-orange-600">
                            {bet.cashoutMultiplier ? `${Number(bet.cashoutMultiplier).toFixed(2)}x` : '-'}
                          </td>
                          <td className={`py-3 px-4 border-r border-gray-200 font-semibold ${colorPL(bet.myPL ?? bet.profitLoss)}`}>
                            {fmtPL(bet.myPL ?? bet.profitLoss)}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                              bet.status === 'won'     ? 'bg-green-100 text-green-700'
                              : bet.status === 'lost'  ? 'bg-red-100 text-red-700'
                              : bet.status === 'cashedout' ? 'bg-orange-100 text-orange-700'
                              : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {bet.status || 'pending'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-400 text-xs whitespace-nowrap">
                            {formatTime(bet.placedAt || bet.createdAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AviatorRoundBetsPage;
